import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Route, Sparkles, Minimize2, Maximize2 } from 'lucide-react';
import { EmeraldAI } from './AIConcierge';
import { TripPlannerQuiz } from './TripPlannerQuiz';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

export function EmeraldAIModal({ open, onOpenChange, defaultTab = 'chat' }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showTripPlanner, setShowTripPlanner] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle opening trip planner - close this modal first
  const handleOpenTripPlanner = () => {
    onOpenChange(false); // Close this modal
    setTimeout(() => {
      setShowTripPlanner(true); // Then open trip planner
    }, 100);
  };

  // Handle trip planner close
  const handleTripPlannerClose = (isOpen) => {
    setShowTripPlanner(isOpen);
  };

  if (!open && !showTripPlanner) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
              onClick={() => onOpenChange(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`fixed z-[101] bg-background rounded-xl shadow-2xl border border-border overflow-hidden ${
                isExpanded 
                  ? 'inset-4 md:inset-8' 
                  : 'bottom-4 right-4 left-4 md:left-auto md:w-[480px] md:max-w-[calc(100vw-2rem)] h-[600px] md:h-[650px]'
              }`}
              data-testid="emerald-ai-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-emerald-900 text-white">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-700 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Emerald AI</h3>
                    <p className="text-xs text-emerald-200">Your personal travel assistant</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white hover:bg-emerald-800"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white hover:bg-emerald-800"
                    onClick={() => onOpenChange(false)}
                    data-testid="close-ai-modal"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-[calc(100%-60px)]">
                <TabsList className="grid grid-cols-2 mx-4 mt-3 bg-muted">
                  <TabsTrigger 
                    value="chat" 
                    className="flex items-center gap-2 text-xs data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                    data-testid="modal-tab-chat"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Chat
                  </TabsTrigger>
                  <TabsTrigger 
                    value="planner" 
                    className="flex items-center gap-2 text-xs data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                    data-testid="modal-tab-planner"
                  >
                    <Route className="w-3.5 h-3.5" />
                    Plan Trip
                  </TabsTrigger>
                </TabsList>

                {/* Chat Tab */}
                <TabsContent value="chat" className="flex-1 overflow-hidden m-0 p-0">
                  <div className="h-full">
                    <EmeraldAI isModal={true} />
                  </div>
                </TabsContent>

                {/* Plan Trip Tab */}
                <TabsContent value="planner" className="flex-1 overflow-auto m-0 p-4">
                  <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                      <Route className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-serif text-xl text-foreground mb-2">Plan Your Koraput Adventure</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                      Answer a few quick questions and get a personalized day-by-day itinerary.
                    </p>
                    <Button
                      onClick={handleOpenTripPlanner}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="modal-start-planner"
                    >
                      <Route className="w-4 h-4 mr-2" />
                      Start Trip Planner
                    </Button>

                    {/* Benefits */}
                    <div className="grid grid-cols-3 gap-3 mt-8 text-center">
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl mb-1">📅</div>
                        <p className="text-xs text-muted-foreground">Custom Itinerary</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl mb-1">📍</div>
                        <p className="text-xs text-muted-foreground">Local Spots</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl mb-1">✨</div>
                        <p className="text-xs text-muted-foreground">AI-Powered</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Trip Planner Quiz Modal - rendered outside the AnimatePresence */}
      <TripPlannerQuiz open={showTripPlanner} onOpenChange={handleTripPlannerClose} />
    </>
  );
}

// Floating Chat Button Component
export function FloatingChatButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    setHasInteracted(true);
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: 'spring', stiffness: 200 }}
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
        data-testid="floating-chat-btn"
        aria-label="Open Emerald AI Chat"
      >
        <Sparkles className="w-6 h-6 group-hover:scale-110 transition-transform" />
        
        {/* Pulse animation for first-time visitors */}
        {!hasInteracted && (
          <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-30" />
        )}
      </motion.button>

      {/* Tooltip */}
      {!hasInteracted && (
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2 }}
          className="fixed bottom-8 right-24 z-50 bg-foreground text-background text-xs px-3 py-2 rounded-lg shadow-lg hidden md:block"
        >
          <span>Chat with Emerald AI</span>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-foreground rotate-45" />
        </motion.div>
      )}

      {/* Modal */}
      <EmeraldAIModal open={isOpen} onOpenChange={setIsOpen} defaultTab="chat" />
    </>
  );
}
